/**
 * The tooth chart: thirty-two adult teeth in FDI notation, each with at
 * most one recorded condition, stored as the text value of the DEN-CHART
 * test. Dependency-free and shared with the SaaS through the
 * `@dru/clinical-math` alias, so the station draws, the API validates and
 * the report prints the same chart.
 *
 * The stored form is JSON: {"14":"CARIOUS","35":"MISSING"}. A tooth with
 * nothing recorded is absent, so an empty chart is "{}" — recorded, and
 * saying every tooth was looked at and found sound.
 */

export const TOOTH_CHART_CODE = 'DEN-CHART';

/** FDI quadrants, upper right → upper left, lower left → lower right, as a chart is read. */
export const FDI_QUADRANTS: ReadonlyArray<{ key: string; label: string; teeth: readonly number[] }> = [
  { key: 'UR', label: 'Upper right', teeth: [18, 17, 16, 15, 14, 13, 12, 11] },
  { key: 'UL', label: 'Upper left', teeth: [21, 22, 23, 24, 25, 26, 27, 28] },
  { key: 'LL', label: 'Lower left', teeth: [31, 32, 33, 34, 35, 36, 37, 38] },
  { key: 'LR', label: 'Lower right', teeth: [41, 42, 43, 44, 45, 46, 47, 48] },
];
export const FDI_TEETH: readonly number[] = FDI_QUADRANTS.flatMap((q) => q.teeth);

export const TOOTH_CONDITIONS = [
  { code: 'CARIOUS', label: 'Carious', short: 'C', tone: 'danger' },
  { code: 'FILLED', label: 'Filled', short: 'F', tone: 'info' },
  { code: 'MISSING', label: 'Missing', short: 'M', tone: 'neutral' },
  { code: 'FRACTURED', label: 'Fractured', short: 'Fr', tone: 'warning' },
  { code: 'MOBILE', label: 'Mobile', short: 'Mo', tone: 'warning' },
  { code: 'CROWN', label: 'Crown / prosthesis', short: 'Cr', tone: 'info' },
  { code: 'ROOT_STUMP', label: 'Root stump', short: 'RS', tone: 'danger' },
  { code: 'IMPACTED', label: 'Impacted', short: 'I', tone: 'warning' },
] as const;
export type ToothConditionCode = (typeof TOOTH_CONDITIONS)[number]['code'];

export type ToothChart = Partial<Record<string, ToothConditionCode>>;

const CONDITION_CODES = new Set<string>(TOOTH_CONDITIONS.map((c) => c.code));
const TOOTH_SET = new Set<string>(FDI_TEETH.map(String));

/** Reads a stored chart. Anything that is not a chart reads as empty, never as an error on a report. */
export function parseToothChart(raw: string | null | undefined): ToothChart {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw) as unknown;
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
    const out: ToothChart = {};
    for (const [tooth, cond] of Object.entries(obj as Record<string, unknown>)) {
      if (TOOTH_SET.has(tooth) && typeof cond === 'string' && CONDITION_CODES.has(cond)) out[tooth] = cond as ToothConditionCode;
    }
    return out;
  } catch { return {}; }
}

/** The stored form: teeth in chart order, only those with a condition. */
export function serialiseToothChart(chart: ToothChart): string {
  const ordered: Record<string, ToothConditionCode> = {};
  for (const tooth of FDI_TEETH) {
    const c = chart[String(tooth)];
    if (c) ordered[String(tooth)] = c;
  }
  return JSON.stringify(ordered);
}

/** True when the value is a well-formed chart (an empty one included). */
export function isToothChart(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  try {
    const obj = JSON.parse(raw) as unknown;
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
    return Object.entries(obj as Record<string, unknown>).every(([t, c]) => TOOTH_SET.has(t) && typeof c === 'string' && CONDITION_CODES.has(c));
  } catch { return false; }
}

/** "14 carious, 35 carious, 46 missing" — or "no tooth findings". */
export function formatToothChart(raw: string | null | undefined): string {
  const chart = parseToothChart(raw);
  const byCondition = new Map<ToothConditionCode, number[]>();
  for (const tooth of FDI_TEETH) {
    const c = chart[String(tooth)];
    if (!c) continue;
    if (!byCondition.has(c)) byCondition.set(c, []);
    byCondition.get(c)!.push(tooth);
  }
  if (!byCondition.size) return 'No tooth findings';
  return TOOTH_CONDITIONS
    .filter((c) => byCondition.has(c.code))
    .map((c) => `${c.label.toLowerCase()}: ${byCondition.get(c.code)!.join(', ')}`)
    .join('; ');
}

/** How many teeth carry a finding, for the station's own count and the report's flag. */
export function toothFindings(raw: string | null | undefined): number {
  return Object.keys(parseToothChart(raw)).length;
}
